import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertOctagon, AlertTriangle, Bell, Info, WifiOff } from 'lucide-react';
import type { Allarme, SeveritaAllarme } from '@emacomfort/domain';
import { useDevice, useObsoleto, useStato } from '@/app/DeviceProvider';
import { useFormato } from '@/app/formato';
import { Avviso, Badge, Bottone, Card, Pagina, Segmenti, type TonoBadge } from '@/ui';
import s from '../features.module.css';

const TONO: Record<SeveritaAllarme, TonoBadge> = { bassa: 'info', media: 'warn', alta: 'err', critica: 'err' };

function IconaSeverita({ severita }: { severita: SeveritaAllarme }) {
  if (severita === 'critica') return <AlertOctagon size={32} aria-hidden />;
  if (severita === 'alta' || severita === 'media') return <AlertTriangle size={32} aria-hidden />;
  return <Info size={32} aria-hidden />;
}

function VoceAllarme({ a }: { a: Allarme }) {
  const { t } = useTranslation();
  const f = useFormato();
  const { client } = useDevice();
  const impianto = useStato((st) => st.impianto);
  const oggetto = impianto?.sorgenti.find((x) => x.id === a.oggetto)?.nome ?? a.oggetto ?? '';
  return (
    <Card>
      <div className={s.allarme}>
        <IconaSeverita severita={a.severita} />
        <div>
          <div className={s.allarmeTitolo}>
            {t(`notifiche.codice.${a.codice}`)}
            {a.oggetto && a.codice !== 'SORGENTE_IRRAGGIUNGIBILE' && ` · ${a.oggetto}`}
          </div>
          <div className={s.allarmeDescrizione}>{t(`notifiche.descrizione.${a.codice}`, { oggetto })}</div>
          <div className={s.letturaNota}>
            {f.dataOra(a.ts)}
            {a.stato === 'riconosciuto' && ` · ${t('notifiche.riconosciuto', { chi: a.riconosciutoDa, ora: f.ora(a.riconosciutoIl) })}`}
            {a.stato === 'chiuso' && ` · ${t('notifiche.chiuso', { ora: f.ora(a.chiusoIl) })}`}
          </div>
        </div>
        <div className={s.lettura} style={{ alignItems: 'flex-end' }}>
          <Badge tono={a.stato === 'chiuso' ? 'neutro' : TONO[a.severita]}>{t(`notifiche.severita.${a.severita}`)}</Badge>
          {a.stato === 'aperto' && (
            <Bottone onClick={() => void client.riconosciAllarme(a.id, impianto?.cliente ?? 'residente')}>{t('notifiche.riconosci')}</Bottone>
          )}
        </div>
      </div>
    </Card>
  );
}

export function NotifichePage() {
  const { t } = useTranslation();
  const f = useFormato();
  const allarmi = useStato((st) => st.allarmi);
  const { obsoleto, ultimaLettura } = useObsoleto();
  const [filtro, setFiltro] = useState<'aperti' | 'tutti'>('aperti');
  const lista = filtro === 'aperti' ? allarmi.filter((a) => a.stato !== 'chiuso') : allarmi;
  return (
    <Pagina titolo={t('notifiche.titolo')}>
      <Segmenti
        etichetta={t('notifiche.titolo')}
        valore={filtro}
        onChange={setFiltro}
        opzioni={[
          { valore: 'aperti', etichetta: t('notifiche.aperti') },
          { valore: 'tutti', etichetta: t('notifiche.tutti') },
        ]}
      />
      {obsoleto && (
        <Card>
          <div className={s.allarme}>
            <WifiOff size={32} aria-hidden />
            <div>
              <div className={s.allarmeTitolo}>{t('notifiche.codice.CENTRALINA_IRRAGGIUNGIBILE')}</div>
              <div className={s.allarmeDescrizione}>{t('notifiche.descrizione.CENTRALINA_IRRAGGIUNGIBILE')}</div>
              <div className={s.letturaNota}>{ultimaLettura ? t('stato.ultimaLettura', { ora: f.ora(ultimaLettura) }) : t('stato.nessunaLettura')}</div>
            </div>
            <Badge tono="err">{t('notifiche.severita.critica')}</Badge>
          </div>
        </Card>
      )}
      {lista.length === 0 && !obsoleto && (
        <Avviso tono="ok">
          <Bell size={18} aria-hidden style={{ verticalAlign: 'middle', marginRight: 8 }} />
          {t('notifiche.nessuna')}
        </Avviso>
      )}
      {lista.map((a) => (
        <VoceAllarme key={a.id} a={a} />
      ))}
    </Pagina>
  );
}
